import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogIn, MapPin, Bell, Globe, ChevronDown, Crosshair, Plus, Check } from "lucide-react";
import { api } from "../lib/api";
import { useTenant } from "../contexts/TenantContext";
import { useAuth } from "../contexts/AuthContext";
import { useChimeOnIncrease } from "../lib/notifSound";
import { languages, t } from "../lib/i18n";
import AddressPickerModal from "./AddressPickerModal";
import RoleSwitcher from "./RoleSwitcher";
import PermissionGateModal from "./PermissionGateModal";
import { toast } from "sonner";
import { getCurrentLocation, requestLocationPermission, checkLocationPermission } from "../lib/geolocation";
import { onAppResume } from "../lib/capacitor";

/** Shared customer header: location picker + language dropdown + bell.
 * Used on Home / Search / Restaurant detail. */
export default function CustomerHeader({ subtitle, children }) {
    const navigate = useNavigate();
    const { lang, setLang } = useTenant();
    const { user } = useAuth();
    const [addrs, setAddrs] = useState([]);
    const [locOpen, setLocOpen] = useState(false);
    const [langOpen, setLangOpen] = useState(false);
    const [picker, setPicker] = useState(false);
    const [notifs, setNotifs] = useState([]);
    // Tracks whether the first ``/notifications`` fetch has resolved —
    // kept separate from ``notifs`` (which stays an array everywhere
    // else in this component) so ``unread`` below can report "not
    // loaded yet" as null instead of a false initial zero. See the
    // ``useChimeOnIncrease`` null-guard for why that distinction matters.
    const [notifsLoaded, setNotifsLoaded] = useState(false);
    const [notifOpen, setNotifOpen] = useState(false);
    // iter102c — location gate modal for wedge-safe permission handling.
    const [locGate, setLocGate] = useState(null); // null | "prompt" | "denied"

    // Re-check permission when the app comes back to the foreground
    // (user switched to device Settings via the "Open device settings"
    // button and back) WHILE the denied-state modal is still showing.
    // Without this, granting the permission in Settings had no visible
    // effect until the user manually re-tapped "Current location" —
    // user-requested: returning to the app should re-check and fetch
    // the location automatically.
    useEffect(() => {
        let unsubscribe = () => {};
        onAppResume(async () => {
            if (locGate !== "denied") return;
            const state = await checkLocationPermission();
            if (state !== "granted") return;
            setLocGate(null);
            const r = await getCurrentLocation();
            if (r.state === "granted" && r.coords) {
                setPicker({ mode: "current", lat: r.coords.lat, lng: r.coords.lng });
            }
        }).then((fn) => { unsubscribe = fn; });
        return () => unsubscribe();
    }, [locGate]);

    const active = useMemo(() => {
        if (!user) return null;
        return addrs.find((a) => a.is_default) || addrs[0] || null;
    }, [addrs, user]);

    const loadAddrs = async () => {
        try {
            const { data } = await api.get("/addresses");
            setAddrs(data || []);
        } catch { /* anonymous user */ }
    };
    const loadNotifs = async () => {
        try {
            const { data } = await api.get("/notifications");
            setNotifs(data || []);
            setNotifsLoaded(true);
        } catch { /* not logged in */ }
    };
    useEffect(() => {
        loadAddrs();
        loadNotifs();
        // Poll every 60s so the bell badge updates without a manual
        // refresh — and so the in-app chime can fire when new pushes
        // arrive (foreground FCM may be silent on some Capacitor builds).
        const t = user ? setInterval(loadNotifs, 60_000) : null;
        const onUpdate = () => loadNotifs();
        window.addEventListener("notifications:update", onUpdate);
        // Capacitor foreground-push hook — chime + badge refresh.
        const onPush = () => loadNotifs();
        window.addEventListener("bizil:push", onPush);
        return () => {
            if (t) clearInterval(t);
            window.removeEventListener("notifications:update", onUpdate);
            window.removeEventListener("bizil:push", onPush);
        };
    }, [user]);

    const setDefault = async (id) => {
        await api.post(`/addresses/${id}/default`);
        await loadAddrs();
        setLocOpen(false);
    };
    const useCurrentLocation = async () => {
        setLocOpen(false);
        // iter102c — audit fix. Migrated from raw navigator.geolocation
        // to the getCurrentLocation() helper which brings:
        //   1. Watchdog timeout so the picker never spins forever.
        //   2. Structured {state,coords,error} return.
        //   3. Wedge-safe path — skips the upfront checkPermissions
        //      call that freezes the JS event loop on production
        //      Android devices (same wedge as iter54.5 + iter102b).
        const r = await getCurrentLocation();
        if (r.state === "granted" && r.coords) {
            setPicker({ mode: "current", lat: r.coords.lat, lng: r.coords.lng });
            return;
        }
        if (r.state === "unsupported") {
            // On desktop web without geolocation, fall through to the
            // manual picker so the user can still add an address.
            return setPicker({ mode: "current" });
        }
        if (r.state === "denied") { setLocGate("denied"); return; }
        if (r.state === "prompt") { setLocGate("prompt"); return; }
        toast.error(r.error || "Couldn't fetch location");
    };

    const unread = notifs.filter((n) => !n.read).length;
    // Audible chime when unread count rises — covers OS-silent scenarios.
    // Reports ``null`` (not "0") until the first fetch resolves, so a
    // fresh mount with pre-existing unread items doesn't look like a
    // brand-new increase (see useChimeOnIncrease's null-guard).
    useChimeOnIncrease(notifsLoaded ? unread : null);
    const langLabel = languages.find((l) => l.code === lang)?.label || "EN";

    return (
        <>
            <header
                data-testid="customer-header"
                className="bg-card px-4 pt-5 pb-3 sticky top-0 z-20 lg:flex lg:items-center lg:gap-6 lg:py-3"
            >
                {/* iter178 — desktop (lg+): one row — location · search · language / role / bell — instead of a phone
                    header with a search bar stretched across the page. ``lg:contents`` dissolves this row into the
                    header's flex line; ``order`` puts the search (rendered after it) in the middle. Below lg nothing changes. */}
                <div className="flex items-start justify-between gap-3 mb-3 lg:contents">
                    {/* Location */}
                    <div className="min-w-0 flex-1 lg:order-first lg:flex-none lg:max-w-xs">
                        <button data-testid="header-location" onClick={() => setLocOpen((v) => !v)}
                            className="flex items-start gap-2 max-w-full w-full text-left">
                            <MapPin size={20} className="text-primary mt-0.5 flex-shrink-0" />
                            <div className="min-w-0">
                                <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold inline-flex items-center gap-1 whitespace-nowrap">
                                    {t("delivered_to", lang) || "Deliver to"} <ChevronDown size={12} />
                                </p>
                                <p className="text-sm font-bold truncate">
                                    {active ? <>
                                        <span className="capitalize">{active.label || "Address"}</span>
                                        <span className="text-muted-foreground font-normal"> • {(active.line1 || "").slice(0, 26)}{(active.line1 || "").length > 26 ? "…" : ""}</span>
                                    </> : <>{user?.first_name || "Set location"}<span className="text-muted-foreground font-normal"> • {subtitle || "Tap to add address"}</span></>}
                                </p>
                            </div>
                        </button>
                    </div>

                    {/* Lang dropdown */}
                    <div className="relative lg:ml-auto">
                        <button data-testid="header-lang" onClick={() => setLangOpen((v) => !v)}
                            className="inline-flex items-center gap-1.5 bg-muted rounded-full px-3 py-2 text-xs font-bold">
                            <Globe size={14} /> {langLabel} <ChevronDown size={12} />
                        </button>
                        {langOpen && (
                            <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-xl shadow-xl py-1 z-30 min-w-[140px]">
                                {languages.map((l) => (
                                    <button data-testid={`lang-pick-${l.code}`} key={l.code}
                                        onClick={() => { setLang(l.code); setLangOpen(false); }}
                                        className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-muted ${lang === l.code ? "font-bold" : ""}`}>
                                        <span>{l.label}</span>
                                        {lang === l.code && <Check size={14} className="text-primary" />}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* iter63c — top-nav role switcher (hidden for guests + single-role users) */}
                    <RoleSwitcher />

                    {/* Bell — logged in only; otherwise show a Login CTA */}
                    {user ? (
                        <button data-testid="header-bell" onClick={() => setNotifOpen(true)}
                            className="relative p-2 rounded-full bg-muted">
                            <Bell size={16} />
                            {unread > 0 && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500" />}
                        </button>
                    ) : (
                        <Link
                            to="/login"
                            data-testid="header-login"
                            className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground rounded-full px-3 py-2 text-xs font-bold whitespace-nowrap"
                        >
                            <LogIn size={14} /> Login
                        </Link>
                    )}
                </div>
                {children && <div className="lg:-order-1 lg:flex-1 lg:min-w-0 lg:max-w-3xl">{children}</div>}
            </header>

            {locOpen && (
                <div className="fixed inset-0 z-50 bg-black/30 flex items-start justify-center pt-20 px-4" onClick={() => setLocOpen(false)}>
                    <div className="bg-card rounded-2xl w-full max-w-md md:max-w-lg p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                        <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-3">Saved addresses</p>
                        <div className="space-y-2">
                            {addrs.length === 0 && <p className="text-sm text-muted-foreground">No saved addresses yet.</p>}
                            {addrs.map((a) => (
                                <button key={a.id} data-testid={`pick-addr-${a.id}`} onClick={() => setDefault(a.id)}
                                    className={`w-full text-left flex items-start gap-3 p-3 rounded-xl border ${active?.id === a.id ? "border-primary bg-primary/5" : "border-border"}`}>
                                    <MapPin size={18} className="mt-0.5 text-primary" />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-sm capitalize">{a.label}{a.is_default && <span className="ml-2 text-[10px] uppercase font-bold text-primary tracking-wider">Default</span>}</p>
                                        <p className="text-xs text-muted-foreground line-clamp-2">{a.line1}{a.line2 ? `, ${a.line2}` : ""} • {a.city} {a.pincode}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-3">
                            <button data-testid="loc-current" onClick={useCurrentLocation} className="inline-flex items-center justify-center gap-2 bg-muted rounded-xl px-3 py-2.5 text-sm font-semibold">
                                <Crosshair size={14} /> Current location
                            </button>
                            <button data-testid="loc-add-new" onClick={() => { setLocOpen(false); setPicker({ mode: "manual" }); }} className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-xl px-3 py-2.5 text-sm font-bold">
                                <Plus size={14} /> Add new
                            </button>
                        </div>
                        <button onClick={() => { setLocOpen(false); navigate("/profile/addresses"); }} className="mt-2 w-full text-center text-xs text-muted-foreground py-1">Manage all addresses</button>
                    </div>
                </div>
            )}

            {picker && (
                <AddressPickerModal
                    initial={picker}
                    onClose={() => setPicker(false)}
                    onSaved={async () => { setPicker(false); await loadAddrs(); }}
                />
            )}

            {notifOpen && (
                <div className="fixed inset-0 z-50 bg-black/30 flex justify-end" onClick={() => setNotifOpen(false)}>
                    <div className="bg-card h-full max-h-none w-full max-w-sm p-4 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <p className="font-bold text-lg">Notifications</p>
                            <button onClick={() => setNotifOpen(false)} className="text-sm text-muted-foreground">Close</button>
                        </div>
                        <div className="mt-3 space-y-2">
                            {notifs.length === 0 && <p className="text-sm text-muted-foreground">No notifications yet.</p>}
                            {notifs.map((n) => (
                                <div key={n.id} data-testid={`notif-${n.id}`} className={`p-3 rounded-xl border ${n.read ? "border-border" : "border-primary/40 bg-primary/5"}`}>
                                    <p className="font-bold text-sm">{n.title}</p>
                                    <p className="text-xs text-muted-foreground mt-0.5">{n.body}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* iter102c — permission-rationale modal for wedge-safe location handling. */}
            {locGate && (
                <PermissionGateModal
                    permission="location"
                    state={locGate}
                    onClose={() => setLocGate(null)}
                    onRequest={async () => {
                        const r = await requestLocationPermission();
                        if (r.state === "granted" && r.coords) {
                            setLocGate(null);
                            setPicker({ mode: "current", lat: r.coords.lat, lng: r.coords.lng });
                        } else if (r.state === "denied") {
                            setLocGate("denied");
                        }
                    }}
                />
            )}
        </>
    );
}
